import { ReactElement, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import colors from '@common/css/cssColors.module.scss';
import { RetailerDocumentType } from '@common/models';
import useWindowSize from '@common/utils/useWindowSize';
import useRetailerDocuments from '@core/hooks/useRetailerDocuments';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import {
    Box,
    Step,
    StepConnector,
    StepContent,
    StepIconProps,
    StepLabel,
    Stepper,
    stepConnectorClasses,
    styled,
} from '@mui/material';
import Stack from '@mui/material/Stack';

import {
    DELIVERY_ICON_PATH,
    FABRICATION_ICON_PATH,
    INSTALL_ICON_PATH,
    PURCHASE_ICON_PATH,
    TELEPHONE_ICON_PATH,
    VERIFICATION_ICON_PATH,
} from './order-summary-constants';

import './what-to-expect.scss';

interface StepData {
    title: string;
    description: string;
}

const HorizontalStepConnector = styled(StepConnector)(() => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 28,
        margin: '0 15px',
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 3,
        border: 0,
        backgroundColor: colors.greyColor,
        borderRadius: 1,
    },
}));

const VerticalStepConnector = styled(StepConnector)(() => ({
    [`& .${stepConnectorClasses.lineVertical}`]: {
        width: 3,
        border: 0,
        backgroundColor: colors.greyColor,
        borderRadius: 1,
        left: 'calc(-50% + 28px)',
        right: 'calc(50% + 28px)',
    },
}));

const StepIconStyle = styled('div')<{
    ownerState: {
        completed?: boolean;
        active?: boolean;
    };
}>(() => ({
    backgroundColor: colors.greyColor,
    zIndex: 1,
    color: colors.greyColor,
    width: 50,
    height: 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
}));

const InstalledStepIcon = ({ active, completed, className, icon }: StepIconProps): JSX.Element => {
    const icons: {
        [index: string]: ReactNode;
    } = {
        1: <img src={PURCHASE_ICON_PATH} alt="purchase-icon" />,
        2: <img src={TELEPHONE_ICON_PATH} alt="telephone-icon" />,
        3: <img src={VERIFICATION_ICON_PATH} alt="verification-icon" />,
        4: <img src={FABRICATION_ICON_PATH} alt="fabrication-icon" />,
        5: <img src={INSTALL_ICON_PATH} alt="install-icon" />,
    };

    return (
        <StepIconStyle ownerState={{ completed, active }} className={className}>
            {icons[String(icon)]}
        </StepIconStyle>
    );
};
const ProductOnlyStepIcon = ({ active, completed, className, icon }: StepIconProps): JSX.Element => {
    const icons: {
        [index: string]: ReactNode;
    } = {
        1: <img src={PURCHASE_ICON_PATH} alt="purchase-icon" />,
        2: <img src={FABRICATION_ICON_PATH} alt="fabrication-icon" />,
        3: <img src={DELIVERY_ICON_PATH} alt="delivery-icon" />,
    };

    return (
        <StepIconStyle ownerState={{ completed, active }} className={className}>
            {icons[String(icon)]}
        </StepIconStyle>
    );
};

const useSteps = (isInstalled: boolean): StepData[] => {
    const { t } = useTranslation();

    const templateChecklistURL = useRetailerDocuments(RetailerDocumentType.TEMPLATE_CHECKLIST, isInstalled);
    const installationChecklistURL = useRetailerDocuments(RetailerDocumentType.INSTALL_CHECKLIST, isInstalled);

    return useMemo(() => {
        const installTypeSuffix = isInstalled ? 'INSTALL' : 'PRODUCT_ONLY';

        return [
            {
                title: t('ORDER_SUMMARY.WHAT_TO_EXPECT.PURCHASE_TITLE'),
                description: <>{t('ORDER_SUMMARY.WHAT_TO_EXPECT.PURCHASE_CONTENT')}</>,
            },
            isInstalled
                ? {
                      title: t('ORDER_SUMMARY.WHAT_TO_EXPECT.SCHEDULING_TITLE'),
                      description: (
                          <>
                              {t('ORDER_SUMMARY.WHAT_TO_EXPECT.SCHEDULING_CONTENT')}
                              <br />
                              <a href={templateChecklistURL} target="_blank" rel="noopener noreferrer" download>
                                  {t('ORDER_SUMMARY.WHAT_TO_EXPECT.SCHEDULING_CONTENT_CLICKABLE')}
                              </a>
                          </>
                      ),
                  }
                : undefined,
            isInstalled
                ? {
                      title: t('ORDER_SUMMARY.WHAT_TO_EXPECT.VERIFICATION_TITLE'),
                      description: <>{t(`ORDER_SUMMARY.WHAT_TO_EXPECT.VERIFICATION_CONTENT.${installTypeSuffix}`)}</>,
                  }
                : undefined,
            {
                title: t('ORDER_SUMMARY.WHAT_TO_EXPECT.FABRICATION_TITLE'),
                description: <>{t('ORDER_SUMMARY.WHAT_TO_EXPECT.FABRICATION_CONTENT')}</>,
            },
            isInstalled
                ? {
                      title: t('ORDER_SUMMARY.WHAT_TO_EXPECT.INSTALL_TITLE'),
                      description: (
                          <>
                              {t('ORDER_SUMMARY.WHAT_TO_EXPECT.INSTALL_CONTENT')}&nbsp;
                              <a href={installationChecklistURL} target="_blank" rel="noopener noreferrer" download>
                                  {t('ORDER_SUMMARY.WHAT_TO_EXPECT.INSTALL_CONTENT_CLICKABLE')}
                              </a>
                          </>
                      ),
                  }
                : {
                      title: t('ORDER_SUMMARY.WHAT_TO_EXPECT.DELIVERY_TITLE'),
                      description: <>{t('ORDER_SUMMARY.WHAT_TO_EXPECT.DELIVERY_CONTENT')}</>,
                  },
        ].filter((item) => !!item) as unknown as StepData[];
    }, [installationChecklistURL, isInstalled, t, templateChecklistURL]);
};

export function WhatToExpect(): ReactElement {
    const { t } = useTranslation();

    const [width] = useWindowSize();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const isInstalled = selectedMaterial?.isInstalled ?? true;

    const steps = useSteps(isInstalled);

    return (
        <div>
            <div className="bottom-info-title">{t('ORDER_SUMMARY.WHAT_TO_EXPECT.TITLE')}</div>
            <>
                {width > 1100 ? (
                    <Stack sx={{ width: '100%' }} className="stepper">
                        <Stepper activeStep={1} alternativeLabel connector={<HorizontalStepConnector />}>
                            {steps.map((step, index) => (
                                <Step key={index} className="what-to-expect-step" active>
                                    <StepLabel
                                        StepIconComponent={isInstalled ? InstalledStepIcon : ProductOnlyStepIcon}
                                    >
                                        <div className="step-title">{step.title}</div>
                                        <div className="step-description">{step.description}</div>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Stack>
                ) : (
                    <Box sx={{ maxWidth: '100%' }} className="stepper">
                        <Stepper orientation="vertical" connector={<VerticalStepConnector />}>
                            {steps.map((step, index) => (
                                <Step key={index} active>
                                    <StepLabel
                                        StepIconComponent={isInstalled ? InstalledStepIcon : ProductOnlyStepIcon}
                                    >
                                        {step.title}
                                    </StepLabel>
                                    <StepContent
                                        className={`${index === steps.length - 1 ? 'last-step' : 'step-info'}`}
                                    >
                                        {step.description}
                                    </StepContent>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                )}
            </>
        </div>
    );
}
